import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Plus, CaretLeft, CaretRight, X, MapPin } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PERMISSIONS } from "@/lib/permissions";
import { useNavigate } from "react-router-dom";

const CAT_STYLE = {
  bar: "bg-[#E0F2FE] text-[#0369A1]",
  festejos: "bg-[#FFE4E6] text-[#BE123C]",
  junta: "bg-[#FFEDD5] text-[#9A3412]",
  otro: "bg-[#E5E7EB] text-[#374151]",
};

const emptyEvento = () => ({
  titulo: "",
  descripcion: "",
  fecha_inicio: new Date().toISOString().slice(0, 10),
  fecha_fin: "",
  lugar: "",
  categoria: "",
  formulario: [],
  requiere_asistencia: false,
  fecha_limite_asistencia: "",
});

const emptyCampo = () => ({
  id: crypto.randomUUID(),
  label: "",
  tipo: "text",
  required: false,
  options: [],
});

const MESES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];
const DIAS_CORTO = ["L", "M", "X", "J", "V", "S", "D"];

export default function Calendario() {
  const [eventos, setEventos] = useState([]);
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return { y: d.getFullYear(), m: d.getMonth() };
  });
  const [modal, setModal] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [managedAsistencias, setManagedAsistencias] = useState({});
  const { can, canScope } = useAuth();

  const canManage = can(PERMISSIONS.MANAGE_EVENTOS);

  const canManageCategoria = (categoria) =>
    canManage && canScope(PERMISSIONS.MANAGE_EVENTOS, "eventos", categoria);

  const canManageEvento = (evento) => canManageCategoria(evento?.categoria);

  const navigate = useNavigate();
  const [showComentarios, setShowComentarios] = useState({});
  const [expandedFamily, setExpandedFamily] = useState(null);

  const fetchAll = async () => {
    const { data } = await api.get("/eventos");
    setEventos(data);
  };
  useEffect(() => {
    fetchAll();
  }, []);

  const loadManageables = async (eventoId) => {
    try {
      const { data } = await api.get(
        `/asistencias/evento/${eventoId}/manageables`,
      );

      const normalized = {};

      data.forEach((item) => {
        normalized[item.fallero.id] = {
          fallero: item.fallero,
          asistencia: item.asistencia,
        };
      });

      setManagedAsistencias((prev) => ({
        ...prev,
        [eventoId]: normalized,
      }));
    } catch (err) {
      toast.error("No se pudo cargar la asistencia");
    }
  };

  const loadAttendanceForEvent = (evento) => {
    if (!evento?.requiere_asistencia) return;
    loadManageables(evento.id);
  };

  const grid = useMemo(() => {
    const first = new Date(cursor.y, cursor.m, 1);
    const startDay = (first.getDay() + 6) % 7; // monday=0
    const daysIn = new Date(cursor.y, cursor.m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDay; i++) cells.push(null);
    for (let d = 1; d <= daysIn; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cursor]);

  const eventsByDay = useMemo(() => {
    const map = {};
    eventos.forEach((e) => {
      const d = new Date(e.fecha_inicio);
      if (d.getFullYear() === cursor.y && d.getMonth() === cursor.m) {
        const key = d.getDate();
        map[key] = map[key] || [];
        map[key].push(e);
      }
    });
    return map;
  }, [eventos, cursor]);

  const move = (delta) => {
    const m = cursor.m + delta;
    const y = cursor.y + Math.floor(m / 12);
    setCursor({ y, m: ((m % 12) + 12) % 12 });
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...modal.data,
        fecha_inicio: modal.data.fecha_inicio,
        fecha_fin: modal.data.fecha_fin ? modal.data.fecha_fin : null,
        fecha_limite_asistencia: modal.data.fecha_limite_asistencia
          ? modal.data.fecha_limite_asistencia
          : null,
        formulario: (modal.data.formulario || []).map((campo) => ({
          id: campo.id,
          label: campo.label,
          tipo: campo.tipo,
          required: campo.required,
          options: (campo.optionsText || "")
            .split(/[\n;]+/)
            .map((v) => v.trim())
            .filter(Boolean),
        })),
      };

      if (modal.isEdit) {
        await api.put(`/eventos/${modal.data.id}`, payload);

        toast.success("Evento actualizado");
      } else {
        await api.post("/eventos", payload);

        toast.success("Evento creado");
      }

      setModal(null);

      fetchAll();
    } catch (err) {
      toast.error("Error: " + (err?.response?.data?.detail || err.message));
    }
  };
  const onDelete = async (e) => {
    if (!window.confirm(`¿Eliminar "${e.titulo}"?`)) return;
    await api.delete(`/eventos/${e.id}`);
    toast.success("Eliminado");
    fetchAll();
  };

  const saveAllAsistencias = async (eventoId) => {
    try {
      const evento = eventos.find((e) => e.id === eventoId);
      const entries = Object.entries(managedAsistencias[eventoId] || {});

      // Solo guardar personas para las que se ha elegido
      // explícitamente "Asistirá" o "No asistirá".
      const pendientes = entries.filter(
        ([, item]) =>
          item?.asistencia?.asistira === true ||
          item?.asistencia?.asistira === false,
      );

      if (pendientes.length === 0) {
        toast.info("No has indicado ninguna asistencia");
        return;
      }

      // Validar preguntas obligatorias
      for (const [falleroId, item] of pendientes) {
        const campoFaltante = getMissingRequiredQuestion(
          evento,
          item.asistencia,
        );

        if (campoFaltante) {
          toast.error(
            `${item.fallero.nombre} debe responder: "${campoFaltante.label}"`,
          );
          return;
        }
      }

      await Promise.all(
        pendientes.map(async ([falleroId, item]) => {
          await api.post("/asistencias", {
            ...item.asistencia,
            evento_id: eventoId,
            fallero_id: falleroId,
          });
        }),
      );

      toast.success("Respuestas guardadas");
      setSelectedDay(null);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "No se pudieron guardar las respuestas",
      );
    }
  };

  const openNewFor = (day) => {
    if (!canManage) {
      // Sin permiso: ignoramos el click en día vacío (no abrir modal de creación).
      return;
    }
    const d = new Date(cursor.y, cursor.m, day, 10, 0)
      .toISOString()
      .slice(0, 16);
    setModal({
      data: {
        ...emptyEvento(),
        fecha_inicio: d,
        categoria: getDefaultCategoria(),
      },
      isEdit: false,
    });
  };

  const addCampoFormulario = () => {
    setModal((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        formulario: [...(prev.data.formulario || []), emptyCampo()],
      },
    }));
  };

  const updateCampoFormulario = (idx, key, value) => {
    const next = structuredClone(modal.data.formulario || []);

    next[idx][key] = value;

    setModal({
      ...modal,
      data: {
        ...modal.data,
        formulario: next,
      },
    });
  };

  const removeCampoFormulario = (idx) => {
    setModal({
      ...modal,
      data: {
        ...modal.data,
        formulario: modal.data.formulario.filter((_, i) => i !== idx),
      },
    });
  };

  const getMissingRequiredQuestion = (evento, asistencia) => {
    if (asistencia?.asistira !== true) {
      return null;
    }

    const respuestas = asistencia?.respuestas || {};

    return (evento?.formulario || []).find((campo) => {
      if (!campo.required) return false;

      const value = respuestas[campo.id];

      if (value === null || value === undefined) {
        return true;
      }

      if (typeof value === "string" && !value.trim()) {
        return true;
      }

      return false;
    });
  };
  const getDefaultCategoria = () => {
    const categorias = Object.keys(CAT_STYLE).filter((categoria) =>
      canManageCategoria(categoria),
    );

    return categorias[0] || "";
  };

  const today = new Date();
  const isToday = (d) =>
    d &&
    today.getFullYear() === cursor.y &&
    today.getMonth() === cursor.m &&
    today.getDate() === d;

  return (
    <div className="p-6 md:p-10" data-testid="calendario-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-med-muted font-semibold mb-2">
            Eventos
          </div>
          <h1 className="font-display text-4xl font-bold text-med-text">
            Calendario
          </h1>
        </div>
        {canManage && (
          <button
            onClick={() =>
              setModal({
                data: {
                  ...emptyEvento(),
                  categoria: getDefaultCategoria(),
                },
                isEdit: false,
              })
            }
            data-testid="add-evento-btn"
            className="inline-flex items-center gap-2 bg-med-primary hover:bg-med-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl px-5 py-2.5 font-semibold active:scale-[0.98]"
          >
            <Plus size={18} weight="bold" />
            Nuevo evento
          </button>
        )}
      </header>

      <div className="bg-med-surface border border-med-border rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-med-border">
          <button
            onClick={() => move(-1)}
            data-testid="cal-prev"
            className="p-2 rounded-lg hover:bg-med-surface2 text-med-text"
          >
            <CaretLeft size={20} weight="bold" />
          </button>
          <h2 className="font-display text-2xl font-bold text-med-text">
            {MESES[cursor.m]} {cursor.y}
          </h2>
          <button
            onClick={() => move(1)}
            data-testid="cal-next"
            className="p-2 rounded-lg hover:bg-med-surface2 text-med-text"
          >
            <CaretRight size={20} weight="bold" />
          </button>
        </div>

        <div className="grid grid-cols-7 bg-med-surface2 text-xs uppercase tracking-[0.1em] text-med-muted font-semibold">
          {DIAS_CORTO.map((d) => (
            <div key={d} className="px-3 py-2 text-center">
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {grid.map((d, i) => {
            const list = (d && eventsByDay[d]) || [];
            const onCellClick = () => {
              if (!d) return;

              if (list.length > 0) {
                setSelectedDay({
                  day: d,
                  eventos: list,
                });

                list.forEach(loadAttendanceForEvent);
              } else if (canManage) {
                openNewFor(d);
              }
            };
            return (
              <div
                key={i}
                onClick={onCellClick}
                className={`min-h-[110px] border-r border-b border-med-border/60 p-2 transition-colors ${
                  d ? "hover:bg-med-surface2/50" : "bg-med-surface2/30"
                } ${d && (canManage || list.length > 0) ? "cursor-pointer" : "cursor-default"}`}
                data-testid={d ? `day-${d}` : undefined}
              >
                {d && (
                  <>
                    <div
                      className={`text-xs font-semibold mb-1 ${
                        isToday(d)
                          ? "inline-flex items-center justify-center w-6 h-6 rounded-full bg-med-primary text-white"
                          : "text-med-text"
                      }`}
                    >
                      {d}
                    </div>
                    <div className="space-y-1">
                      {list.slice(0, 3).map((e) => (
                        <div
                          key={e.id}
                          onClick={(ev) => {
                            ev.stopPropagation();
                            setSelectedDay({
                              day: d,
                              eventos: list,
                            });

                            list.forEach(loadAttendanceForEvent);
                          }}
                          className={`text-[11px] truncate px-1.5 py-0.5 rounded ${CAT_STYLE[e.categoria]}`}
                        >
                          {e.titulo}
                        </div>
                      ))}
                      {list.length > 3 && (
                        <div className="text-[10px] text-med-muted">
                          +{list.length - 3} más
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Day events list */}
      {selectedDay && (
        <div
          className="fixed inset-0 bg-med-text/40 backdrop-blur-sm grid place-items-center z-50 p-4"
          onClick={() => setSelectedDay(null)}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-med-surface rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
          >
            <div className="px-6 py-4 border-b border-med-border flex items-center justify-between">
              <h3 className="font-display text-xl font-bold">
                {selectedDay.day} de {MESES[cursor.m]}
              </h3>
              <button
                onClick={() => setSelectedDay(null)}
                className="p-1.5 rounded-lg hover:bg-med-surface2"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-3 overflow-y-auto flex-1">
              {selectedDay.eventos.map((e) => {
                const asistenciaCerrada =
                  e.fecha_limite_asistencia &&
                  new Date() > new Date(e.fecha_limite_asistencia);

                return (
                  <div
                    key={e.id}
                    className="border border-med-border rounded-xl p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span
                            className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded ${CAT_STYLE[e.categoria]}`}
                          >
                            {e.categoria}
                          </span>
                          <span className="text-xs text-med-muted">
                            {new Date(e.fecha_inicio).toLocaleTimeString(
                              "es-ES",
                              { hour: "2-digit", minute: "2-digit" },
                            )}
                          </span>
                        </div>
                        <h4 className="font-bold text-med-text">{e.titulo}</h4>
                        {e.lugar && (
                          <div className="flex items-center gap-1 text-xs text-med-muted mt-1">
                            <MapPin size={12} weight="duotone" /> {e.lugar}
                          </div>
                        )}
                        {e.descripcion && (
                          <p className="text-sm text-med-muted mt-2 whitespace-pre-line">
                            {e.descripcion}
                          </p>
                        )}
                      </div>
                    </div>
                    {e.requiere_asistencia &&
                      (asistenciaCerrada ? (
                        <>
                          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                            El plazo de inscripción ha finalizado
                          </div>
                        </>
                      ) : (
                        <div className="mt-4 border-t border-med-border pt-4 space-y-4">
                          {Object.entries(managedAsistencias[e.id] || {}).map(
                            ([falleroId, item]) => {
                              const asistencia = item.asistencia;
                              const fallero = item.fallero;

                              return (
                                <div
                                  key={falleroId}
                                  className="border border-med-border rounded-2xl mb-4 bg-med-surface2/40 overflow-hidden"
                                >
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setExpandedFamily(
                                        expandedFamily ===
                                          `${e.id}-${falleroId}`
                                          ? null
                                          : `${e.id}-${falleroId}`,
                                      )
                                    }
                                    className="w-full flex items-center justify-between px-4 py-4 text-left hover:bg-med-surface2 transition-colors"
                                  >
                                    <div>
                                      <div className="font-bold text-med-text">
                                        {fallero.nombre} {fallero.apellidos}
                                      </div>

                                      <div className="text-xs text-med-muted mt-1">
                                        {asistencia?.asistira === true
                                          ? "Asistirá"
                                          : asistencia?.asistira === false
                                            ? "No asistirá"
                                            : "Pendiente de respuesta"}
                                      </div>
                                    </div>

                                    <div className="text-xl text-med-muted font-light">
                                      {expandedFamily === `${e.id}-${falleroId}`
                                        ? "−"
                                        : "+"}
                                    </div>
                                  </button>

                                  {expandedFamily ===
                                    `${e.id}-${falleroId}` && (
                                    <motion.div
                                      initial={{
                                        height: 0,
                                        opacity: 0,
                                      }}
                                      animate={{
                                        height: "auto",
                                        opacity: 1,
                                      }}
                                      transition={{
                                        duration: 0.2,
                                      }}
                                      className="px-4 pb-4 overflow-hidden"
                                    >
                                      <div className="flex gap-2 mb-4">
                                        <button
                                          onClick={() =>
                                            setManagedAsistencias((prev) => ({
                                              ...prev,
                                              [e.id]: {
                                                ...(prev[e.id] || {}),
                                                [falleroId]: {
                                                  ...item,
                                                  asistencia: {
                                                    ...asistencia,
                                                    asistira: true,
                                                  },
                                                },
                                              },
                                            }))
                                          }
                                          className={`px-3 py-2 rounded-xl text-sm font-semibold ${
                                            asistencia?.asistira === true
                                              ? "bg-emerald-600 text-white"
                                              : "bg-med-surface2"
                                          }`}
                                        >
                                          Asistirá
                                        </button>

                                        <button
                                          onClick={() =>
                                            setManagedAsistencias((prev) => ({
                                              ...prev,
                                              [e.id]: {
                                                ...(prev[e.id] || {}),
                                                [falleroId]: {
                                                  ...item,
                                                  asistencia: {
                                                    ...asistencia,
                                                    asistira: false,
                                                  },
                                                },
                                              },
                                            }))
                                          }
                                          className={`px-3 py-2 rounded-xl text-sm font-semibold ${
                                            asistencia?.asistira === false
                                              ? "bg-red-600 text-white"
                                              : "bg-med-surface2"
                                          }`}
                                        >
                                          No asistirá
                                        </button>
                                      </div>

                                      {/* formulario dinámico */}
                                      {asistencia?.asistira === true &&
                                        e.formulario?.length > 0 && (
                                          <div className="space-y-4">
                                            {e.formulario.map((campo) => (
                                              <div key={campo.id}>
                                                <label className="block text-xs font-semibold uppercase tracking-wide text-med-muted mb-1">
                                                  {campo.label}
                                                  {campo.required ? " *" : ""}
                                                </label>

                                                {/* text */}
                                                {campo.tipo === "text" && (
                                                  <input
                                                    type="text"
                                                    value={
                                                      asistencia?.respuestas?.[
                                                        campo.id
                                                      ] || ""
                                                    }
                                                    onChange={(ev) =>
                                                      setManagedAsistencias(
                                                        (prev) => ({
                                                          ...prev,
                                                          [e.id]: {
                                                            ...(prev[e.id] ||
                                                              {}),
                                                            [falleroId]: {
                                                              ...item,
                                                              asistencia: {
                                                                ...asistencia,
                                                                respuestas: {
                                                                  ...(asistencia.respuestas ||
                                                                    {}),
                                                                  [campo.id]:
                                                                    ev.target
                                                                      .value,
                                                                },
                                                              },
                                                            },
                                                          },
                                                        }),
                                                      )
                                                    }
                                                    className="w-full rounded-xl bg-med-surface2 px-3 py-2"
                                                  />
                                                )}

                                                {/* textarea */}
                                                {campo.tipo === "textarea" && (
                                                  <textarea
                                                    rows={3}
                                                    value={
                                                      asistencia?.respuestas?.[
                                                        campo.id
                                                      ] || ""
                                                    }
                                                    onChange={(ev) =>
                                                      setManagedAsistencias(
                                                        (prev) => ({
                                                          ...prev,
                                                          [e.id]: {
                                                            ...(prev[e.id] ||
                                                              {}),
                                                            [falleroId]: {
                                                              ...item,
                                                              asistencia: {
                                                                ...asistencia,
                                                                respuestas: {
                                                                  ...(asistencia.respuestas ||
                                                                    {}),
                                                                  [campo.id]:
                                                                    ev.target
                                                                      .value,
                                                                },
                                                              },
                                                            },
                                                          },
                                                        }),
                                                      )
                                                    }
                                                    className="w-full rounded-xl bg-med-surface2 px-3 py-2"
                                                  />
                                                )}

                                                {/* number */}
                                                {campo.tipo === "number" && (
                                                  <input
                                                    type="number"
                                                    value={
                                                      asistencia?.respuestas?.[
                                                        campo.id
                                                      ] || ""
                                                    }
                                                    onChange={(ev) =>
                                                      setManagedAsistencias(
                                                        (prev) => ({
                                                          ...prev,
                                                          [e.id]: {
                                                            ...(prev[e.id] ||
                                                              {}),
                                                            [falleroId]: {
                                                              ...item,
                                                              asistencia: {
                                                                ...asistencia,
                                                                respuestas: {
                                                                  ...(asistencia.respuestas ||
                                                                    {}),
                                                                  [campo.id]:
                                                                    ev.target
                                                                      .value,
                                                                },
                                                              },
                                                            },
                                                          },
                                                        }),
                                                      )
                                                    }
                                                    className="w-full rounded-xl bg-med-surface2 px-3 py-2"
                                                  />
                                                )}

                                                {/* checkbox */}
                                                {campo.tipo === "checkbox" && (
                                                  <label className="flex items-center gap-2">
                                                    <input
                                                      type="checkbox"
                                                      checked={
                                                        asistencia
                                                          ?.respuestas?.[
                                                          campo.id
                                                        ] || false
                                                      }
                                                      onChange={(ev) =>
                                                        setManagedAsistencias(
                                                          (prev) => ({
                                                            ...prev,
                                                            [e.id]: {
                                                              ...(prev[e.id] ||
                                                                {}),
                                                              [falleroId]: {
                                                                ...item,
                                                                asistencia: {
                                                                  ...asistencia,
                                                                  respuestas: {
                                                                    ...(asistencia.respuestas ||
                                                                      {}),
                                                                    [campo.id]:
                                                                      ev.target
                                                                        .checked,
                                                                  },
                                                                },
                                                              },
                                                            },
                                                          }),
                                                        )
                                                      }
                                                    />
                                                    Sí
                                                  </label>
                                                )}

                                                {/* select */}
                                                {campo.tipo === "select" && (
                                                  <select
                                                    value={
                                                      asistencia?.respuestas?.[
                                                        campo.id
                                                      ] || ""
                                                    }
                                                    onChange={(ev) =>
                                                      setManagedAsistencias(
                                                        (prev) => ({
                                                          ...prev,
                                                          [e.id]: {
                                                            ...(prev[e.id] ||
                                                              {}),
                                                            [falleroId]: {
                                                              ...item,
                                                              asistencia: {
                                                                ...asistencia,
                                                                respuestas: {
                                                                  ...(asistencia.respuestas ||
                                                                    {}),
                                                                  [campo.id]:
                                                                    ev.target
                                                                      .value,
                                                                },
                                                              },
                                                            },
                                                          },
                                                        }),
                                                      )
                                                    }
                                                    className="w-full rounded-xl bg-med-surface2 px-3 py-2"
                                                  >
                                                    <option value="">
                                                      Selecciona...
                                                    </option>

                                                    {(campo.options || []).map(
                                                      (opt) => (
                                                        <option
                                                          key={opt}
                                                          value={opt}
                                                        >
                                                          {opt}
                                                        </option>
                                                      ),
                                                    )}
                                                  </select>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        )}

                                      {/* comentario */}
                                      <div className="mt-3 mb-4">
                                        {!(
                                          showComentarios[
                                            `${e.id}-${falleroId}`
                                          ] || asistencia?.comentario
                                        ) ? (
                                          <button
                                            type="button"
                                            onClick={() =>
                                              setShowComentarios((prev) => ({
                                                ...prev,

                                                [`${e.id}-${falleroId}`]: true,
                                              }))
                                            }
                                            className="
                                                                                text-sm
                                                                                text-med-secondary
                                                                                hover:underline
                                                                            "
                                          >
                                            Añadir comentario
                                          </button>
                                        ) : (
                                          <>
                                            <div className="flex items-center justify-between mb-1">
                                              <label className="block text-xs font-semibold uppercase tracking-wide text-med-muted">
                                                Comentario
                                              </label>

                                              {!asistencia?.comentario && (
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    setShowComentarios(
                                                      (prev) => ({
                                                        ...prev,
                                                        [`${e.id}-${falleroId}`]: false,
                                                      }),
                                                    )
                                                  }
                                                  className="text-xs text-med-muted hover:text-med-text"
                                                >
                                                  Ocultar
                                                </button>
                                              )}
                                            </div>

                                            <textarea
                                              rows={2}
                                              value={
                                                asistencia?.comentario || ""
                                              }
                                              onChange={(ev) =>
                                                setManagedAsistencias(
                                                  (prev) => ({
                                                    ...prev,
                                                    [e.id]: {
                                                      ...(prev[e.id] || {}),
                                                      [falleroId]: {
                                                        ...item,
                                                        asistencia: {
                                                          ...asistencia,
                                                          comentario:
                                                            ev.target.value,
                                                        },
                                                      },
                                                    },
                                                  }),
                                                )
                                              }
                                              className="w-full rounded-xl bg-med-surface2 px-3 py-2"
                                              placeholder="Añade un comentario..."
                                            />
                                          </>
                                        )}
                                      </div>
                                    </motion.div>
                                  )}
                                </div>
                              );
                            },
                          )}

                          <button
                            onClick={() => saveAllAsistencias(e.id)}
                            className="w-full bg-med-primary hover:bg-med-primary-hover text-white rounded-xl py-3 text-sm font-semibold mt-2"
                          >
                            Guardar respuestas
                          </button>
                        </div>
                      ))}

                    <div className="flex gap-2 mt-3">
                      {e.requiere_asistencia && (
                        <button
                          onClick={() =>
                            navigate(`/eventos/${e.id}/asistencia`)
                          }
                          className="text-xs px-3 py-1.5 rounded-lg bg-med-secondary text-white hover:opacity-90"
                        >
                          Ver asistentes
                        </button>
                      )}
                      {canManageEvento(e) ? (
                        <>
                          <button
                            onClick={() => {
                              const di = e.fecha_inicio.slice(0, 16);
                              const df = e.fecha_fin
                                ? e.fecha_fin.slice(0, 16)
                                : "";
                              const dl = e.fecha_limite_asistencia
                                ? e.fecha_limite_asistencia.slice(0, 16)
                                : "";
                              setSelectedDay(null);
                              setModal({
                                data: {
                                  ...e,
                                  fecha_inicio: di,
                                  fecha_fin: df,
                                  fecha_limite_asistencia: dl,
                                  formulario: (e.formulario || []).map(
                                    (campo) => ({
                                      ...campo,
                                      optionsText: (campo.options || []).join(
                                        "\n",
                                      ),
                                    }),
                                  ),
                                },
                                isEdit: true,
                              });
                            }}
                            data-testid={`edit-evento-${e.id}`}
                            className="text-xs px-3 py-1.5 rounded-lg border border-med-border hover:border-med-secondary"
                          >
                            Editar
                          </button>
                          <button
                            onClick={async () => {
                              await onDelete(e);
                              setSelectedDay(null);
                            }}
                            data-testid={`delete-evento-${e.id}`}
                            className="text-xs px-3 py-1.5 rounded-lg text-med-danger hover:bg-med-danger/10"
                          >
                            Eliminar
                          </button>
                        </>
                      ) : (
                        <span></span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
      )}

      {/* Form modal */}
      {modal && (
        <div
          className="fixed inset-0 bg-med-text/40 backdrop-blur-sm grid place-items-center z-50 p-4"
          onClick={() => setModal(null)}
        >
          <motion.form
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-med-surface rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            data-testid="evento-modal"
          >
            <div className="px-6 py-4 border-b border-med-border flex items-center justify-between">
              <h3 className="font-display text-xl font-bold">
                {modal.isEdit ? "Editar evento" : "Nuevo evento"}
              </h3>
              <button
                type="button"
                onClick={() => setModal(null)}
                className="p-1.5 rounded-lg hover:bg-med-surface2"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                  Título *
                </label>
                <input
                  required
                  value={modal.data.titulo}
                  onChange={(e) =>
                    setModal({
                      ...modal,
                      data: { ...modal.data, titulo: e.target.value },
                    })
                  }
                  data-testid="evento-titulo"
                  className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                    Inicio *
                  </label>
                  <input
                    type="datetime-local"
                    required
                    value={modal.data.fecha_inicio}
                    onChange={(e) =>
                      setModal({
                        ...modal,
                        data: { ...modal.data, fecha_inicio: e.target.value },
                      })
                    }
                    className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                    Fin
                  </label>
                  <input
                    type="datetime-local"
                    value={modal.data.fecha_fin || ""}
                    onChange={(e) =>
                      setModal({
                        ...modal,
                        data: { ...modal.data, fecha_fin: e.target.value },
                      })
                    }
                    className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                    Categoría
                  </label>
                  <select
                    value={modal.data.categoria}
                    onChange={(e) =>
                      setModal({
                        ...modal,
                        data: {
                          ...modal.data,
                          categoria: e.target.value,
                        },
                      })
                    }
                    className="w-full rounded-xl bg-med-surface2 px-3 py-2"
                  >
                    {Object.keys(CAT_STYLE)
                      .filter((categoria) => canManageCategoria(categoria))
                      .map((categoria) => (
                        <option key={categoria} value={categoria}>
                          {categoria.charAt(0).toUpperCase() +
                            categoria.slice(1)}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                    Lugar
                  </label>
                  <input
                    value={modal.data.lugar || ""}
                    onChange={(e) =>
                      setModal({
                        ...modal,
                        data: { ...modal.data, lugar: e.target.value },
                      })
                    }
                    className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                  Descripción
                </label>
                <textarea
                  rows={3}
                  value={modal.data.descripcion || ""}
                  onChange={(e) =>
                    setModal({
                      ...modal,
                      data: { ...modal.data, descripcion: e.target.value },
                    })
                  }
                  className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
                />
                <div className="flex items-center gap-3 pt-2">
                  <input
                    type="checkbox"
                    checked={modal.data.requiere_asistencia}
                    onChange={(e) =>
                      setModal({
                        ...modal,
                        data: {
                          ...modal.data,
                          requiere_asistencia: e.target.checked,

                          fecha_limite_asistencia: e.target.checked
                            ? modal.data.fecha_limite_asistencia
                            : "",
                        },
                      })
                    }
                  />

                  <label className="text-sm font-medium text-med-text">
                    Requiere confirmación de asistencia
                  </label>
                </div>
                {modal.data.requiere_asistencia && (
                  <div className="border-t border-med-border pt-5 mt-4">
                    <div className="mt-4">
                      <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                        Fecha límite inscripción *
                      </label>

                      <input
                        type="datetime-local"
                        required
                        value={modal.data.fecha_limite_asistencia || ""}
                        onChange={(e) =>
                          setModal({
                            ...modal,
                            data: {
                              ...modal.data,
                              fecha_limite_asistencia: e.target.value,
                            },
                          })
                        }
                        className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
                      />

                      <p className="text-xs text-med-muted mt-2">
                        Después de esta fecha ya no se podrá modificar la
                        asistencia
                      </p>
                    </div>

                    <div className="flex items-center justify-between mb-4 mt-8">
                      <div>
                        <h4 className="font-display text-lg font-bold text-med-text">
                          Formulario inscripción
                        </h4>

                        <p className="text-xs text-med-muted mt-1">
                          Preguntas opcionales para apuntarse al evento
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={addCampoFormulario}
                        className="inline-flex items-center gap-2 bg-med-secondary text-white rounded-xl px-4 py-2 text-sm font-semibold"
                      >
                        <Plus size={16} weight="bold" />
                        Añadir pregunta
                      </button>
                    </div>

                    <div className="space-y-4">
                      {(modal.data.formulario || []).map((campo, idx) => (
                        <div
                          key={campo.id}
                          className="rounded-2xl border border-med-border p-4 bg-med-surface2"
                        >
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-semibold uppercase tracking-wide text-med-muted mb-1">
                                Pregunta
                              </label>

                              <input
                                value={campo.label}
                                onChange={(e) =>
                                  updateCampoFormulario(
                                    idx,
                                    "label",
                                    e.target.value,
                                  )
                                }
                                className="w-full rounded-xl bg-white px-4 py-2.5"
                                placeholder="Ej: ¿Qué bocadillo quieres?"
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-semibold uppercase tracking-wide text-med-muted mb-1">
                                Tipo
                              </label>

                              <select
                                value={campo.tipo}
                                onChange={(e) =>
                                  updateCampoFormulario(
                                    idx,
                                    "tipo",
                                    e.target.value,
                                  )
                                }
                                className="w-full rounded-xl bg-white px-4 py-2.5"
                              >
                                <option value="text">Texto</option>

                                <option value="textarea">Texto largo</option>

                                <option value="select">Selección</option>

                                <option value="number">Número</option>

                                <option value="checkbox">Sí/No</option>
                              </select>
                            </div>
                          </div>

                          {campo.tipo === "select" && (
                            <div className="mt-4">
                              <label className="block text-xs font-semibold uppercase tracking-wide text-med-muted mb-1">
                                Opciones
                              </label>

                              <textarea
                                rows={3}
                                value={campo.optionsText || ""}
                                onChange={(e) =>
                                  updateCampoFormulario(
                                    idx,
                                    "optionsText",
                                    e.target.value,
                                  )
                                }
                                className="w-full rounded-xl bg-white px-4 py-2.5"
                                placeholder={`Tortilla\nLomo\nVegetal`}
                              />
                            </div>
                          )}

                          <div className="flex items-center justify-between mt-4">
                            <label className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={campo.required}
                                onChange={(e) =>
                                  updateCampoFormulario(
                                    idx,
                                    "required",
                                    e.target.checked,
                                  )
                                }
                              />
                              Obligatoria
                            </label>

                            <button
                              type="button"
                              onClick={() => removeCampoFormulario(idx)}
                              className="text-sm text-red-600 hover:underline"
                            >
                              Eliminar
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-med-border flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setModal(null)}
                className="px-5 py-2.5 rounded-xl border-2 border-med-border text-med-text hover:border-med-primary hover:text-med-primary font-medium"
              >
                Cancelar
              </button>
              <button
                type="submit"
                data-testid="save-evento-btn"
                className="px-5 py-2.5 rounded-xl bg-med-primary hover:bg-med-primary-hover text-white font-semibold"
              >
                {modal.isEdit ? "Guardar" : "Crear"}
              </button>
            </div>
          </motion.form>
        </div>
      )}
    </div>
  );
}
